import { ReactNode, useMemo, useState } from 'react';
import { GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';
import { RfqId } from '#/unified-rfqs/entities/rfq';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { useUMPlatformCreationDisabled } from '#/platform-state/contexts/providers';

interface QuoteBuilderModalMethods {
  readonly open: (params: OpenParams) => void;
  readonly close: () => void;
  readonly setLegPricing: (enabled: boolean) => void;
}

interface OpenParams {
  readonly rfqId: RfqId;
  readonly clickedQuote: GrfqQuote | null;
  readonly side: 'BUY' | 'SELL';
  readonly quantity?: string;
  readonly price?: string;
}

interface QuoteBuilderModalStates {
  readonly rfqId?: RfqId;
  readonly isOpen: boolean;
  readonly legPricingEnabled: boolean;
  /* Indicates if modal was open by a bid button (buy) or ask button (sell) */
  readonly clickSide: 'BUY' | 'SELL';
  readonly clickedQuote: GrfqQuote | null;
  readonly quantity?: string;
  readonly price?: string;
}

export const [useQuoteBuilderModalState, ModalStateProvider] =
  createSafeContext<QuoteBuilderModalStates>('QuoteBuilderModalState');

export const [useQuoteBuilderModalMethods, ModalMethodsProvider] =
  createSafeContext<QuoteBuilderModalMethods>('QuoteBuilderModalMethods');

interface Props {
  readonly children: ReactNode;
}

export const QuoteBuilderModalProvider = ({ children }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [forcedLegPricing, setForcedLegPricing] = useState(false);
  const [clickSide, setClickSide] = useState<'BUY' | 'SELL'>('BUY');
  const [clickedQuote, setClickedQuote] = useState<GrfqQuote | null>(null);
  const [selectedRfqId, setSelectedRfqId] = useState<RfqId>();
  const [quantity, setQuantity] = useState<string>();
  const [price, setPrice] = useState<string>();

  const methods = useMemo(
    (): QuoteBuilderModalMethods => ({
      open: ({ rfqId, side, clickedQuote: nextClickedQuote, ...props }) => {
        setIsOpen(true);
        setClickSide(side);
        setSelectedRfqId(rfqId);
        setClickedQuote(nextClickedQuote);
        setQuantity(props.quantity);
        setPrice(props.price);
      },
      close: () => {
        setIsOpen(false);
        setForcedLegPricing(false);
        setQuantity(undefined);
        setPrice(undefined);
      },
      setLegPricing: (visible) => setForcedLegPricing(visible),
    }),
    [],
  );

  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const state: QuoteBuilderModalStates = useMemo(
    () => ({
      isOpen: isPlatformCreationDisabled ? false : isOpen,
      legPricingEnabled: forcedLegPricing,
      rfqId: selectedRfqId,
      clickSide,
      clickedQuote,
      quantity,
      price,
    }),
    [
      clickSide,
      clickedQuote,
      forcedLegPricing,
      isOpen,
      isPlatformCreationDisabled,
      price,
      quantity,
      selectedRfqId,
    ],
  );

  return (
    <ModalStateProvider value={state}>
      <ModalMethodsProvider value={methods}>{children}</ModalMethodsProvider>
    </ModalStateProvider>
  );
};
